import { useCallback, useEffect, useMemo, useState } from 'react';
import { Search, X } from 'lucide-react';
import type { FilterMeta, ResourceMeta } from '../../types/resource';
import { ResourceLocationFilter } from './ResourceLocationFilter';

interface ResourceGridFiltersProps {
  meta: ResourceMeta;
  /**
   * Current filter values keyed by their query-string parameter (the
   * `param` field on `FilterMeta`, or one of `district` / `subdistrict`
   * / `village` for the composite `location` filter).
   */
  values: Record<string, string>;
  onChange: (values: Record<string, string>) => void;
}

/**
 * Filter bar built dynamically from `meta.view_meta.filters[]`.
 *
 * Each filter's `type` picks the widget:
 *
 *   - `like_any` → debounced free-text search
 *   - `eq`       → text / date / select depending on the filter's title
 *                  (`_date` suffix or `type: date` on the matching
 *                  field → date picker; otherwise text)
 *   - `location` → three inputs (District / Subdistrict / Village)
 *                  emitted as separate query params
 *
 * The value map is flat so the page state stays a simple
 * `Record<string, string>`. Empty strings are stripped before they
 * hit the query string (see `filterQuery.toQueryParams`).
 */
export function ResourceGridFilters({ meta, values, onChange }: ResourceGridFiltersProps) {
  const filters = useMemo(() => meta.view_meta?.filters ?? [], [meta]);
  const fieldTypeByTitle = useMemo(() => {
    const map = new Map<string, string>();
    for (const f of meta.view_meta?.fields ?? []) {
      if (typeof f.type === 'string') map.set(f.title, f.type);
    }
    return map;
  }, [meta]);

  const activeCount = Object.values(values).filter((v) => v !== '' && v !== undefined).length;

  const setParam = useCallback(
    (param: string, next: string) => {
      const nextValues = { ...values };
      if (next === '') delete nextValues[param];
      else nextValues[param] = next;
      onChange(nextValues);
    },
    [onChange, values],
  );

  const clearAll = useCallback(() => onChange({}), [onChange]);

  if (filters.length === 0) return null;

  return (
    <div className="flex flex-wrap items-end gap-3 rounded-xl border border-rgf-gray-200 bg-white p-3 shadow-sm">
      {filters.map((filter) => (
        <FilterControl
          key={filter.title}
          filter={filter}
          fieldType={filter.param ? fieldTypeByTitle.get(filter.param) : undefined}
          values={values}
          onChange={setParam}
        />
      ))}

      {activeCount > 0 && (
        <button
          type="button"
          onClick={clearAll}
          className="ml-auto inline-flex items-center gap-1 rounded-lg border border-rgf-gray-200 px-2.5 py-1.5 text-xs font-medium text-rgf-gray-600 hover:border-rgf-gray-300 hover:text-rgf-gray-900"
        >
          <X className="h-3.5 w-3.5" />
          Clear filters
        </button>
      )}
    </div>
  );
}

interface FilterControlProps {
  filter: FilterMeta;
  fieldType: string | undefined;
  values: Record<string, string>;
  onChange: (param: string, value: string) => void;
}

function FilterControl({ filter, fieldType, values, onChange }: FilterControlProps) {
  const label = humaniseLabel(filter.title);

  if (filter.type === 'location') {
    return (
      <ResourceLocationFilter
        label={label}
        values={values}
        onChange={(param, v) => onChange(param, v)}
      />
    );
  }

  if (filter.type === 'like_any') {
    return (
      <SearchFilter
        label={label}
        param={filter.param ?? filter.title}
        value={values[filter.param ?? filter.title] ?? ''}
        description={filter.description}
        onCommit={onChange}
      />
    );
  }

  // `eq`, `in`, and anything else — text-ish input by default.
  const param = filter.param ?? filter.title;
  const current = values[param] ?? '';

  // Date picker when either the filter itself is date-typed or the
  // linked field's `type` is `date`. A "_date" suffix on the title is
  // a soft signal we use as a last resort.
  const isDate =
    fieldType === 'date' ||
    fieldType === 'datetime' ||
    filter.title.endsWith('_date') ||
    filter.title === 'date';

  if (isDate) {
    return (
      <LabelledField label={label} description={filter.description}>
        <input
          type="date"
          value={current}
          onChange={(e) => onChange(param, e.target.value)}
          className="rounded-lg border border-rgf-gray-300 px-2.5 py-1.5 text-sm focus:border-rgf-primary-500 focus:outline-none focus:ring-1 focus:ring-rgf-primary-500"
        />
      </LabelledField>
    );
  }

  if (Array.isArray(filter.options) && filter.options.length > 0) {
    return (
      <LabelledField label={label} description={filter.description}>
        <select
          value={current}
          onChange={(e) => onChange(param, e.target.value)}
          className="w-40 rounded-lg border border-rgf-gray-300 bg-white px-2.5 py-1.5 text-sm focus:border-rgf-primary-500 focus:outline-none focus:ring-1 focus:ring-rgf-primary-500"
        >
          <option value="">All</option>
          {filter.options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label || o.value}
            </option>
          ))}
        </select>
      </LabelledField>
    );
  }

  return (
    <LabelledField label={label} description={filter.description}>
      <input
        type="text"
        value={current}
        onChange={(e) => onChange(param, e.target.value)}
        placeholder="Any"
        className="w-40 rounded-lg border border-rgf-gray-300 px-2.5 py-1.5 text-sm focus:border-rgf-primary-500 focus:outline-none focus:ring-1 focus:ring-rgf-primary-500"
      />
    </LabelledField>
  );
}

/**
 * Debounced text search box. Keystrokes update the local state
 * immediately for UI responsiveness and commit to the parent (which
 * triggers a network request) 300ms after the user stops typing.
 */
function SearchFilter({
  label,
  param,
  value,
  description,
  onCommit,
}: {
  label: string;
  param: string;
  value: string;
  description?: string;
  onCommit: (param: string, value: string) => void;
}) {
  const [local, setLocal] = useState(value);
  useEffect(() => setLocal(value), [value]);
  useEffect(() => {
    const handle = window.setTimeout(() => {
      if (local !== value) onCommit(param, local);
    }, 300);
    return () => window.clearTimeout(handle);
  }, [local, value, param, onCommit]);

  return (
    <label className="flex flex-col text-xs">
      <span className="mb-1 font-medium text-rgf-gray-600">{label}</span>
      <div className="relative">
        <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-rgf-gray-400" />
        <input
          type="search"
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          placeholder="Search…"
          title={description}
          className="w-56 rounded-lg border border-rgf-gray-300 py-1.5 pl-8 pr-3 text-sm focus:border-rgf-primary-500 focus:outline-none focus:ring-1 focus:ring-rgf-primary-500"
        />
      </div>
    </label>
  );
}

function LabelledField({
  label,
  description,
  children,
}: {
  label: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col text-xs" title={description}>
      <span className="mb-1 font-medium text-rgf-gray-600">{label}</span>
      {children}
    </label>
  );
}

function humaniseLabel(title: string): string {
  return title
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
